<template>
  <div
    ref="curtain"
    class="curtain-wrapper"
    @animationend="onEnd"
    @click="onClose"
  >
    <div class="scroll-wrapper">
      <div
        class="curtain"
        @click.stop
        @scroll="(e) => $emit('curtain-scroll', e)"
      >
        <slot />
      </div>
      <div class="icon-close" @click="onClose">
        <UiIcon name="close" size="24" hover-stroke="#363ef8" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
});
const emit = defineEmits(['close', 'curtain-scroll']);
const curtain = ref<HTMLElement>();
const closedState = ref<boolean>(false);

watch(
  () => props.open,
  (open) => {
    if (!import.meta.env.SSR && curtain.value) {
      if (open) {
        curtain.value.classList.add('open');
        const scrollHeight =
          curtain.value?.offsetWidth - curtain.value.clientWidth;
        document.body.style.paddingRight = scrollHeight + 'px';
      } else {
        closedState.value = true;
        curtain.value.classList.remove('open');
        curtain.value.classList.add('closed');
        document.body.style.paddingRight = '';
      }
    }
  }
);

const onClose = (): void => {
  if (props.open && !closedState.value) {
    emit('close');
  }
};

const onEnd = (): void => {
  if (closedState.value && curtain.value) {
    curtain.value.classList.remove('closed');
    closedState.value = false;
  }
};
</script>
<style scoped lang="scss">
.curtain-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 100;
  background-color: transparent;
  width: 100vw;
  justify-content: flex-start;
  align-items: stretch;
  display: none;
  overflow-y: hidden;
  @include md-block {
    overflow: scroll;
    -webkit-overflow-scrolling: touch;
  }
  @include lg-block {
    display: block;
    position: static;
    z-index: inherit;
    top: initial;
    left: initial;
    right: initial;
    bottom: initial;
    overflow: visible;
    @include col-all(6);
  }
  @include xl-block {
    @include col-all(5);
  }
  &.open {
    animation: 0.3s mobile-appearance;
    display: flex;
    background-color: rgba(0, 0, 0, 0.3);
    @include md-block {
      animation: 0.3s appearance;
    }
    @include lg-block {
      display: block;
      animation: none;
    }

    .scroll-wrapper {
      animation: none;

      @include md-block {
        animation: 0.3s slide-in;
      }

      @include lg-block {
        animation: none;
      }
    }
  }

  &.closed {
    animation: 0.3s mobile-exit;
    display: flex;
    @include md-block {
      animation: 0.3s exit;
    }
    @include lg-block {
      display: block;
      animation: none;
    }

    .scroll-wrapper {
      animation: none;
      @include md-block {
        animation: 0.3s slide-out;
      }
      @include lg-block {
        animation: none;
      }
    }
  }

  .scroll-wrapper {
    width: 100%;
    position: relative;
    @include md-block {
      width: 320px;
    }
    @include lg-block {
      width: auto;
    }

    .icon-close {
      height: 40px;
      width: 40px;
      top: 0;
      right: 0;
      z-index: 3;
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      cursor: pointer;
      @include md-block {
        top: 20px;
        right: 20px;
        width: 20px;
        height: 20px;
      }
      @include lg-block {
        display: none;
      }
    }

    .curtain {
      height: 100%;
      width: 100%;
      overflow-y: scroll;
      position: relative;
      scrollbar-width: none;
      -ms-overflow-style: none;
      background-color: $white;

      @include lg-block {
        overflow-y: unset;
      }
      &::-webkit-scrollbar {
        display: none;
      }
    }
  }
}
@keyframes appearance {
  from {
    background-color: transparent;
  }

  to {
    background-color: rgba(0, 0, 0, 0.3);
  }
}

@keyframes slide-in {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(0);
  }
}

@keyframes slide-out {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-100%);
  }
}

@keyframes exit {
  from {
    background-color: rgba(0, 0, 0, 0.3);
  }

  to {
    background-color: transparent;
  }
}

@keyframes mobile-exit {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

@keyframes mobile-appearance {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.sticky-on {
  position: sticky !important;
  top: 10px !important;
  z-index: 9 !important;
  height: fit-content;
  @include lg-block {
    position: initial;
    top: unset;
    z-index: unset;
    height: initial;
  }
}
</style>
